import { useCallback, useEffect, useRef } from "react";
import {
  completed,
  failed,
  fromStatusResponse,
  isTerminal,
  reconcileAction,
  withProcessing,
} from "../domain/mediaUpload";
import { fetchUploadStatus } from "../services/mediaUpload";
import useSocket from "./useSocket";

const EVENTS = Object.freeze({
  PROGRESS: "mediaUpload:progress",
  COMPLETE: "mediaUpload:complete",
  ERROR: "mediaUpload:error",
});

const POLL_INTERVAL_MS = 30_000;

/* Socket events while the connection holds, plus a poll of the upload's row to
   close the gap when one is missed. Holds no state of its own. */
const useMediaUploadProgress = ({ mediaUploadId, state, onAction }) => {
  const actionRef = useRef(onAction);
  actionRef.current = onAction;

  const stateRef = useRef(state);
  stateRef.current = state;

  const forThisUpload = useCallback(
    (toAction) => (data) => {
      if (!mediaUploadId || data?.mediaUploadId !== mediaUploadId) return;
      actionRef.current(toAction(data));
    },
    [mediaUploadId],
  );

  useSocket(
    mediaUploadId ? EVENTS.PROGRESS : null,
    forThisUpload((data) =>
      withProcessing({ percent: data.percent, stage: data.stage }),
    ),
  );

  useSocket(
    mediaUploadId ? EVENTS.COMPLETE : null,
    forThisUpload((data) =>
      completed({
        processedKey: data.processedKey,
        processedUrl: data.processedUrl,
      }),
    ),
  );

  useSocket(
    mediaUploadId ? EVENTS.ERROR : null,
    forThisUpload((data) =>
      failed({
        error: data.error,
        errorClassification: data.errorClassification,
        errorCode: data.errorCode,
        errorLimit: data.errorLimit,
      }),
    ),
  );

  const reconcile = useCallback(async () => {
    if (!mediaUploadId) return;

    const snapshot = fromStatusResponse(await fetchUploadStatus(mediaUploadId));
    const action = reconcileAction(stateRef.current, snapshot);

    if (action) actionRef.current(action);
  }, [mediaUploadId]);

  const { status } = state;

  useEffect(() => {
    if (!mediaUploadId || isTerminal(status)) return undefined;

    reconcile();

    const timer = setInterval(reconcile, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [mediaUploadId, status, reconcile]);

  // A reconnect is the moment a missed event is most likely, so ask rather than
  // wait out the interval.
  const socket = useSocket(mediaUploadId ? "connect" : null, reconcile);

  return socket;
};

export default useMediaUploadProgress;
